import {
  MdAdd,
  MdDarkMode,
  MdLightMode,
  MdLogout,
  MdMenu,
  MdMenuOpen,
  MdNotificationsActive,
  MdNotificationsNone,
  MdOutlineAttachment,
  MdOutlineQueryStats,
  MdOutlineRepeat,
  MdOutlineSwapVert,
  MdRefresh,
  MdSettings,
} from "react-icons/md";
import BlocksNavigation from "../Navigation/BlocksNavigation";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import modalsStore from "../../store/modalsStore";
import { useLocation } from "react-router-dom";
import useMainFilter from "../../hooks/useMainFilter";
import useDarkMode from "../../hooks/useDarkMode";
import useInvites from "../../hooks/useInvites";
import { ICON_SIZES } from "../../helpers/constants";
import { useMemo } from "react";
import useCollapsibles from "../../hooks/useCollapsibles";
import useRecurringTransactions from "src/hooks/useRecurringTransactions";
import useAllTransactions from "src/hooks/useAllTransactions";

const DesktopNavigation: React.FC = () => {
  const { collapsiblePreferences, updateCollapsible } = useCollapsibles();

  const { setShowLogoutDialog, showLogoutDialog } = modalsStore();
  const { isEnabled, setEnabled } = useDarkMode();
  const { receivedInvites } = useInvites();
  const { t } = useTranslation();
  const { mainFilter } = useMainFilter();
  const { pathname } = useLocation();
  const { allPendingTransactions, allReceivedTransactions } =
    useAllTransactions();

  const { recentOverduePayments, upcomingPayments, todayPayments } =
    useRecurringTransactions(false);
  const iconSize = collapsiblePreferences.navIsCollapsed
    ? ICON_SIZES.LG
    : ICON_SIZES.LG;

  const isActiveLink = (link: string) =>
    pathname?.startsWith(link) || `${pathname}/`.startsWith(`${link}/`);

  const handleToggleMenu = () => {
    updateCollapsible({
      navIsCollapsed: !collapsiblePreferences.navIsCollapsed,
    });
  };

  const hasNotifications = useMemo(() => {
    return (
      allPendingTransactions?.length +
      recentOverduePayments?.length +
      upcomingPayments?.length +
      todayPayments?.length +
      allReceivedTransactions?.length
    );
  }, [
    recentOverduePayments,
    upcomingPayments,
    todayPayments,
    allReceivedTransactions,
    allPendingTransactions,
  ]);

  const links = useMemo(
    () => [
      {
        link: `/transaction/${mainFilter?.account || ""}`,
        text: t("main_menu.new_transaction"),
        icon: <MdAdd size={iconSize} />,
        active: isActiveLink(`/transaction/${mainFilter?.account || ""}`),
      },
      {
        link: `/notifications`,
        text: t("main_menu.notifications"),
        icon: !!hasNotifications ? (
          <MdNotificationsActive size={iconSize} />
        ) : (
          <MdNotificationsNone size={iconSize} />
        ),
        active: isActiveLink(`/notifications`),
        notify: !!hasNotifications,
      },
      {
        link: `/transactions/${mainFilter?.account || ""}`,
        text: t("main_menu.transactions"),
        icon: <MdOutlineSwapVert size={iconSize} />,
        active: isActiveLink(`/transactions/${mainFilter?.account || ""}`),
      },
      {
        link: `/reports/${mainFilter?.account || ""}`,
        text: t("main_menu.stats"),
        icon: <MdOutlineQueryStats size={iconSize} />,
        active: isActiveLink(`/reports/${mainFilter?.account || ""}`),
      },
      {
        link: `/recurring/${mainFilter?.account || ""}`,
        text: t("main_menu.recurring_transactions"),
        icon: <MdOutlineRepeat size={iconSize} />,
        active: isActiveLink(`/recurring/${mainFilter?.account || ""}`),
      },
      {
        link: `/attachments/${mainFilter?.account || ""}`,
        text: t("main_menu.files"),
        icon: <MdOutlineAttachment size={iconSize} />,
        active: isActiveLink(`/attachments/${mainFilter?.account || ""}`),
      },
      {
        link: "/settings",
        text: t("main_menu.settings"),
        icon: <MdSettings size={iconSize} />,
        active: [
          pathname === "/settings",
          pathname === "/settings/profile",
          pathname === "/settings/accounts",
          pathname === "/settings/categories",
          pathname === "/settings/sources",
          pathname === "/settings/payment-methods",
          pathname === "/settings/voice-assistants",
          pathname === "/settings/cache",
          pathname === "/settings/colors",
          pathname === "/settings/help",
        ]?.includes(true),
        // hide: collapsiblePreferences.navIsCollapsed,
      },

      {
        onClick: () => window.location.reload(),
        text: t("main_menu.reload"),
        icon: <MdRefresh size={iconSize} />,
      },
      {
        onClick: () => setEnabled(!isEnabled),
        text: t(isEnabled ? "main_menu.light_mode" : "main_menu.dark_mode"),
        icon: isEnabled ? (
          <MdLightMode size={iconSize} className="text-yellow-400" />
        ) : (
          <MdDarkMode size={iconSize} className="text-purple-400" />
        ),
      },
      {
        onClick: handleToggleMenu,
        text: collapsiblePreferences.navIsCollapsed
          ? ""
          : t("main_menu.hide_menu"),
        icon: collapsiblePreferences.navIsCollapsed ? (
          <MdMenu size={iconSize} />
        ) : (
          <MdMenuOpen size={iconSize} />
        ),
        tooltip: t(
          collapsiblePreferences.navIsCollapsed ? "main_menu.show_menu" : ""
        ),
      },
      {
        onClick: () => setShowLogoutDialog(true),
        text: t("main_menu.logout"),
        icon: (
          <MdLogout
            size={iconSize}
            className={clsx({ "text-red-500": showLogoutDialog })}
          />
        ),
        active: showLogoutDialog,
      },
    ],
    [
      iconSize,
      collapsiblePreferences.navIsCollapsed,
      isEnabled,
      mainFilter?.account,
      pathname,
      receivedInvites?.length,
      hasNotifications,
      t,
    ]
  );

  return <BlocksNavigation links={links} />;
};

export default DesktopNavigation;
